import { FC, MouseEvent, ChangeEvent, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import PropTypes from "prop-types";
import { TableSortLabel, Box, IconButton, InputBase } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

interface DataTableProps {
  onPageChange: (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onRowsPerPageChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onSearch: (value: string) => void;
  hasPermission?: boolean;
  onRowClick?: (row: any) => void;
  page: number;
  items: any[];
  columns: any[];
  itemsCount: number;
  rowsPerPage: number;
  hasSearch?: boolean;
}
type Order = "asc" | "desc";

export const DataTable: FC<DataTableProps> = ({
  onPageChange,
  onRowsPerPageChange,
  onSearch,
  onRowClick,
  page,
  items,
  columns,
  itemsCount,
  rowsPerPage,
  hasSearch,
  ...other
}) => {
  const [order, setOrder] = useState<Order>("asc");
  const [search, setSearch] = useState<string>("");
  const [orderBy, setOrderBy] = useState<string>("");

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      handleRequestSort(event, property);
    };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const onSearchInput = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    onSearch(event.target.value);
  };

  const clearSearch = () => {
    setSearch("");
    onSearch("");
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      {hasSearch && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "10px",
          }}
        >
          <InputBase
            sx={{ ml: 1 }}
            placeholder="Search"
            value={search}
            inputProps={{ "aria-label": "search" }}
            onChange={onSearchInput}
          />
          {search ? (
            <IconButton
              color="primary"
              aria-label="clear search"
              onClick={clearSearch}
            >
              <ClearIcon />
            </IconButton>
          ) : (
            <IconButton aria-label="clear search">
              <SearchIcon />
            </IconButton>
          )}
        </div>
      )}
      <TableContainer sx={{ maxHeight: 440 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={createSortHandler(column.id)}
                  >
                    {column.label}
                    {orderBy === column.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {items
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .sort(getComparator(order, orderBy))
              .map((row, index) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {columns.map((column) => {
                      const value = row[column.id] ?? row;
                      return (
                        <TableCell
                          key={`${column.id}-${index}`}
                          align={column.align}
                        >
                          {column.format || column.id === "cta"
                            ? column.format(value)
                            : value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={itemsCount}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 40, 50]}
        />
      </TableContainer>
    </Paper>
  );
};

DataTable.propTypes = {
  items: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  itemsCount: PropTypes.number.isRequired,
  onRowsPerPageChange: PropTypes.func,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};
